<template>
  <div class="main row">
    <div class="col-md-12">
      <KTCodePreview v-bind:title="'Quản lý ngân sách'">
        <template v-slot:toolbar>
          <b-button
            v-if="checkPermission('BUDGET_INSERT')"
            variant="primary"
            size="sm"
            class="mr-2"
            @click="declare"
          >
            <i class="fas fa-plus"></i> Khai báo NS
          </b-button>
          <b-button
            v-if="checkPermission('BUDGET_IMPORT')"
            variant="success"
            size="sm"
            @click="importExcel"
          >
            <i class="fas fa-file-excel"></i> Import Excel
          </b-button>
        </template>

        <template v-slot:preview>
          <!-- Filters -->
          <b-row class="mb-3">
            <b-col cols="12" md="2">
              <date-picker
                v-if="viewMode === 'month'"
                v-model="filterPeriodFrom"
                :config="dpConfigs.month"
                class="form-control-sm"
                placeholder="Từ tháng"
              ></date-picker>
              <date-picker
                v-else
                v-model="filterYear"
                :config="dpConfigs.year"
                class="form-control-sm"
                placeholder="Năm"
              ></date-picker>
            </b-col>
            <b-col cols="12" md="2" v-if="viewMode === 'month'">
              <date-picker
                v-model="filterPeriodTo"
                :config="dpConfigs.month"
                class="form-control-sm"
                placeholder="Đến tháng"
              ></date-picker>
            </b-col>
            <b-col cols="12" md="3">
              <BDropdownCustom
                v-model="filterDepartmentId"
                :options="departmentDropdownOptions"
                :placeholder="'phòng ban'"
                :matchKey="['name']"
                :searchable="true"
                :compareById="true"
                :limit="5"
              />
            </b-col>
            <b-col cols="12" md="3">
              <BDropdownCustom
                v-model="filterCostItemId"
                :options="costItemDropdownOptions"
                :placeholder="'khoản mục'"
                :matchKey="['name']"
                :searchable="true"
                :compareById="true"
                :limit="5"
              />
            </b-col>
            <b-col cols="12" md="2">
              <BDropdownCustom
                v-model="filterStoreId"
                :options="storeDropdownOptions"
                :placeholder="'cửa hàng'"
                :matchKey="['name']"
                :searchable="true"
                :compareById="true"
                :limit="5"
              />
            </b-col>
          </b-row>

          <b-row class="mb-4">
            <b-col cols="12" md="2">
              <b-form-select
                v-model="filterCompanyId"
                :options="companyOptions"
                size="sm"
              />
            </b-col>
            <b-col cols="12" md="3" class="mt-2 mt-md-0">
              <BDropdownCustom
                v-model="filterCostGroupId"
                :options="costGroupDropdownOptions"
                :placeholder="'nhóm phí'"
                :matchKey="['name']"
                :searchable="true"
                :limit="5"
              />
            </b-col>
            <b-col cols="12" md="3" class="mt-2 mt-md-0">
              <b-form-select
                v-model="filterStatus"
                :options="statusOptions"
                size="sm"
              />
            </b-col>
            <b-col cols="12" md="4" class="mt-2 mt-md-0">
              <Autosuggest
                :model.sync="employeeModel"
                :suggestions="employeeSuggestions"
                :limit="suggestLimit"
                suggestionName="suggestionName"
                placeholder="người tạo"
                @select="onSelectedEmployee"
                @change="onSearchEmployeeChange"
              />
            </b-col>
          </b-row>
          <b-row class="mb-3">
            <b-col cols="12" class="d-flex align-items-center">
              <b-form-checkbox
                v-model="filterMyApproval"
                @change="onFilter"
              >
                Cần tôi duyệt
              </b-form-checkbox>
            </b-col>
          </b-row>

          <!-- View mode switch (separate row, above table) -->
          <b-row class="mb-3">
            <b-col cols="12" md="2" class="mt-2 mt-md-0 d-flex align-items-end">
              <b-button variant="primary" size="sm" @click="onFilter">
                <i class="fas fa-search"></i> Lọc
              </b-button>
            </b-col>
            <b-col cols="12" class="d-flex justify-content-end">
              <b-button-group size="sm">
                <b-button
                  :variant="viewMode === 'year' ? 'primary' : 'outline-primary'"
                  @click="setViewMode('year')"
                >
                  Xem theo năm
                </b-button>
                <b-button
                  :variant="
                    viewMode === 'month' ? 'primary' : 'outline-primary'
                  "
                  @click="setViewMode('month')"
                >
                  Xem theo tháng
                </b-button>
              </b-button-group>
            </b-col>
          </b-row>

          <!-- Table -->
          <b-table
            :items="items"
            :fields="fields"
            bordered
            hover
            :busy="onLoading"
          >
            <template #cell(index)="data">
              {{ (page - 1) * 20 + data.index + 1 }}
            </template>
            <template #cell(period)="data">
              {{
                viewMode === 'year'
                  ? formatYear(data.value)
                  : formatPeriod(data.value)
              }}
            </template>
            <template #cell(info)="row">
              <div>
                <div>
                  <span class="font-weight-bold">Doanh nghiệp:</span>
                  <span class="ml-1">{{
                    row.item.companyName || row.item.companyId
                  }}</span>
                </div>
                <div>
                  <span class="font-weight-bold">Phòng ban:</span>
                  <span class="ml-1">{{ row.item.departmentName || '' }}</span>
                </div>
                <div>
                  <span class="font-weight-bold">Khoản mục:</span>
                  <span class="ml-1">{{ row.item.costItemName || '' }}</span>
                </div>
                <div>
                  <span class="font-weight-bold">Cửa hàng:</span>
                  <span class="ml-1">{{ row.item.storeName || '' }}</span>
                </div>
              </div>
            </template>
            <template #cell(allocatedAmount)="data">
              {{ convertPrice(data.value) }}
            </template>
            <template #cell(usedAmount)="data">
              {{ convertPrice(data.value) }}
            </template>
            <template #cell(remainingAmount)="data">
              <span
                :class="data.value < 0 ? 'text-danger font-weight-bold' : ''"
              >
                {{ convertPrice(data.value) }}
              </span>
            </template>
            <template #cell(status)="row">
              <b-badge :variant="statusVariant(row.item.status)">
                {{ statusName(row.item.status) }}
              </b-badge>
            </template>
            <template #cell(nextApprovalStep)="data">
              <ApprovalStepBadge :step="data.value" />
            </template>
            <template v-slot:cell(actions)="row">
              <div class="d-flex justify-content-center">
                <b-dropdown size="sm" id="dropdown-left" no-caret right>
                  <template #button-content>
                    <i class="flaticon2-settings"></i>
                  </template>
                  <b-dropdown-item @click="viewDetail(row.item)">
                    <span style="color: #3f4254; font-size: 10px">
                      <i class="flaticon2-sheet icon-size-sm"></i>
                      &nbsp; Chi tiết
                    </span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="
                      viewMode === 'year' &&
                      row.item.canApprove 
                    "
                    @click="
                      openApprovalModal(
                        row.item,
                        WORKFLOW_APPROVAL_STEP_STATUS.APPROVED,
                      )
                    "
                  >
                    <span style="color: #3f4254; font-size: 10px">
                      <i class="flaticon2-checkmark icon-size-sm"></i>
                      &nbsp; Duyệt
                    </span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="
                      viewMode === 'year' &&
                      row.item.canApprove
                    "
                    @click="
                      openApprovalModal(
                        row.item,
                        WORKFLOW_APPROVAL_STEP_STATUS.REJECTED,
                      )
                    "
                  >
                    <span style="color: #3f4254; font-size: 10px">
                      <i class="flaticon2-cancel icon-size-sm"></i>
                      &nbsp; Không duyệt
                    </span>
                  </b-dropdown-item>
                  <b-dropdown-item @click="editItem(row.item)">
                    <span style="color: #3f4254; font-size: 10px">
                      <i class="flaticon2-pen icon-size-sm"></i>
                      &nbsp; Sửa
                    </span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="
                      checkPermission('BUDGET_DELETE') && canDeleteBudgetLine(row.item)
                    "
                    @click="confirmDeleteBudget(row.item)"
                  >
                    <span style="color: #f64e60; font-size: 10px">
                      <i class="flaticon2-trash icon-size-sm"></i>
                      &nbsp; Xóa
                    </span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="viewMode === 'month'"
                    @click="viewHistory(row.item)"
                  >
                    <span style="color: #3f4254; font-size: 10px">
                      <i class="flaticon2-time icon-size-sm"></i>
                      &nbsp; Lịch sử
                    </span>
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </template>
          </b-table>

          <!-- Dòng tổng cộng -->
          <b-row v-if="items.length" class="mb-3">
            <b-col class="d-flex justify-content-end">
              <span class="mr-4">
                <strong>Tổng phân bổ: </strong>
                <span class="text-primary">{{
                  convertPrice(totalAllocated)
                }}</span>
              </span>
              <span class="mr-4">
                <strong>Tổng đã dùng: </strong>
                <span>{{ convertPrice(totalUsed) }}</span>
              </span>
              <span>
                <strong>Tổng còn lại: </strong>
                <span :class="totalRemaining < 0 ? 'text-danger' : ''">{{
                  convertPrice(totalRemaining)
                }}</span>
              </span>
            </b-col>
          </b-row>

          <b-row class="mt-3 align-items-center">
            <b-col cols="12" md="4">
              Tổng số: <strong>{{ totalRow }}</strong>
            </b-col>
            <b-col cols="12" md="8" class="d-flex justify-content-end">
              <BPaginationNavCustom
                :currentPage="page"
                :numberOfPages="numberOfPage"
                :totalPage="numberOfPage"
                :linkParams="paginationLinkParams"
                @page-change="fetchData"
              />
            </b-col>
          </b-row>

          <!-- Modal duyệt / không duyệt NS -->
          <b-modal
            ref="approvalModal"
            :title="
              approvalAction === WORKFLOW_APPROVAL_STEP_STATUS.APPROVED
                ? 'Duyệt ngân sách'
                : 'Không duyệt ngân sách'
            "
            @ok="confirmApproval"
            ok-title="Xác nhận"
            cancel-title="Đóng"
            :ok-variant="
              approvalAction === WORKFLOW_APPROVAL_STEP_STATUS.APPROVED
                ? 'success'
                : 'danger'
            "
          >
            <b-form-group>
              <template #label>
                <span
                  v-if="
                    approvalAction === WORKFLOW_APPROVAL_STEP_STATUS.APPROVED
                  "
                  >Ghi chú duyệt</span
                >
                <span v-else
                  >Lý do không duyệt <span class="text-danger">*</span></span
                >
              </template>
              <b-form-textarea
                v-model="approvalForm.note"
                rows="3"
                :placeholder="
                  approvalAction === WORKFLOW_APPROVAL_STEP_STATUS.APPROVED
                    ? 'Ghi chú (nếu có)'
                    : 'Nhập lý do không duyệt'
                "
              />
            </b-form-group>
          </b-modal>

          <!-- Modal lịch sử -->
          <b-modal
            ref="historyModal"
            title="Lịch sử thay đổi"
            size="lg"
            ok-only
            ok-title="Đóng"
          >
            <b-table
              :items="historyItems"
              :fields="historyFields"
              bordered
              hover
              small
            >
              <template #cell(createdAt)="data">
                {{ formatDateTime(data.value) }}
              </template>
              <template #cell(updatedBy)="data">
                {{ data.item.updatedByName || data.value }}
              </template>
            </b-table>
          </b-modal>
        </template>
      </KTCodePreview>
    </div>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import { checkPermission, getData } from '@/utils/saveDataToLocal';
import {
  makeToastSuccess,
  makeToastFaile,
  convertPrice,
  handleError,
  xoa_dau,
} from '@/utils/common';
import { showDeleteAlert } from '@/utils/sweet-alert2';
import moment from 'moment';
import timeUtils from '@/utils/date';
import BDropdownCustom from '@/view/base/bootstrap/BDropdownCustom.vue';
import BPaginationNavCustom from '@/view/base/bootstrap/BPaginationNavCustom.vue';
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import ApprovalStepBadge from '@/view/base/bootstrap/ApprovalStepBadge.vue';
import {
  BUDGET_STATUS,
  BUDGET_STATUS_BADGE_VARIANT,
  WORKFLOW_APPROVAL_STEP_STATUS,
} from '@/utils/enum';
import {
  BUDGET_STATUS_NAME,
  HISTORY_ACTION_TYPE_CODE,
} from '@/utils/enum-name';

export default {
  components: { KTCodePreview, BDropdownCustom, BPaginationNavCustom, Autosuggest, ApprovalStepBadge },
  data() {
    return {
      BUDGET_STATUS,
      WORKFLOW_APPROVAL_STEP_STATUS,
      viewMode: 'year',
      items: [],
      onLoading: false,
      page: 1,
      numberOfPage: 1,
      totalRow: 0,
      filterDepartmentId: [],
      filterCostItemId: [],
      filterStoreId: [],
      filterPeriodFrom: '',
      filterPeriodTo: '',
      filterYear: moment(String(new Date().getFullYear()), 'YYYY'),
      filterStatus: null,
      filterCompanyId: null,
      filterCostGroupId: [],
      filterCreatedBy: null,
      filterMyApproval: false,
      currentEmployeeId: null,
      filterKeyword: '',
      employeeModel: '',
      suggestLimit: 50,
      employeeSource: [],
      employeeSuggestions: [],
      companyOptions: [{ value: null, text: '-- Doanh nghiệp --' }],
      costGroupDropdownOptions: [],
      costItemDropdownOptions: [],
      departmentDropdownOptions: [],
      storeDropdownOptions: [],
      statusOptions: [
        { value: null, text: '-- Trạng thái --' },
        // BudgetLine có trạng thái DRAFT = 1. Ở UI này ta không còn hiển thị label "Nháp"
        // mà hiển thị như "Chờ duyệt" để đồng bộ luồng tạo xong -> duyệt.
        {
          value: BUDGET_STATUS.DRAFT,
          text: BUDGET_STATUS_NAME[BUDGET_STATUS.DRAFT],
        },
        {
          value: BUDGET_STATUS.BROWSING,
          text: BUDGET_STATUS_NAME[BUDGET_STATUS.BROWSING],
        },
        {
          value: BUDGET_STATUS.APPROVED,
          text: BUDGET_STATUS_NAME[BUDGET_STATUS.APPROVED],
        },
        {
          value: BUDGET_STATUS.LOCKED,
          text: BUDGET_STATUS_NAME[BUDGET_STATUS.LOCKED],
        },
        {
          value: BUDGET_STATUS.REJECTED,
          text: BUDGET_STATUS_NAME[BUDGET_STATUS.REJECTED],
        },
      ],
      dpConfigs: {
        month: {
          ...timeUtils.DP_CONFIG.date,
          format: 'YYYY-MM',
          viewMode: 'months',
          allowInputToggle: false,
        },
        year: {
          ...timeUtils.DP_CONFIG.date,
          format: 'YYYY',
          viewMode: 'years',
          allowInputToggle: false,
        },
      },
      historyItems: [],
      historyFields: [
        { key: 'createdAt', label: 'Thời gian', thStyle: { width: '15%' } },
        { key: 'actionLabel', label: 'Hành động', thStyle: { width: '15%' } },
        { key: 'description', label: 'Chi tiết' },
        {
          key: 'originalValueText',
          label: 'Giá trị cũ',
          thStyle: { width: '22%' },
        },
        {
          key: 'newValueText',
          label: 'Giá trị mới',
          thStyle: { width: '22%' },
        },
        {
          key: 'updatedBy',
          label: 'Người thực hiện',
          thStyle: { width: '10%' },
        },
      ],
      approvalAction: WORKFLOW_APPROVAL_STEP_STATUS.APPROVED,
      approvalForm: { id: null, note: '' },
      fields: [
        {
          key: 'index',
          label: 'STT',
          thStyle: { textAlign: 'center', width: '4%' },
          class: 'text-center',
        },
        {
          key: 'period',
          label: 'Kỳ',
          thStyle: { textAlign: 'center', width: '8%' },
          class: 'text-center',
        },
        {
          key: 'info',
          label: 'Thông tin',
          thStyle: { textAlign: 'left', width: '26%' },
        },
        {
          key: 'allocatedAmount',
          label: 'Phân bổ',
          thStyle: { textAlign: 'center', width: '12%' },
          tdClass: 'text-right',
        },
        {
          key: 'usedAmount',
          label: 'Đã dùng',
          thStyle: { textAlign: 'center', width: '12%' },
          tdClass: 'text-right',
        },
        {
          key: 'remainingAmount',
          label: 'Còn lại',
          thStyle: { textAlign: 'center', width: '10%' },
          tdClass: 'text-right',
        },
        {
          key: 'nextApprovalStep',
          label: 'Bước duyệt',
          thStyle: { textAlign: 'center', width: '10%' },
          class: 'text-center',
        },
        {
          key: 'status',
          label: 'Trạng thái',
          thStyle: { textAlign: 'center', width: '9%' },
          class: 'text-center',
        },
        {
          key: 'createdByName',
          label: 'Người tạo',
          thStyle: { textAlign: 'center', width: '9%' },
          class: 'text-center',
        },
        {
          key: 'actions',
          label: '',
          thStyle: { width: '5%' },
          class: 'text-center',
        },
      ],
      linkGen: (pageNum) => (pageNum === 1 ? '?' : `?page=${pageNum}`),
    };
  },
  computed: {
    paginationLinkParams() {
      const common = {
        view: this.viewMode,
        companyId: this.filterCompanyId || '',
        costGroupId: this.filterCostGroupId.map((g) => g.id),
        status: this.filterStatus !== null ? this.filterStatus : '',
        createdBy: this.filterCreatedBy || '',
        keyword: this.filterKeyword || '',
        departmentId: this.filterDepartmentId.map((d) => d.id),
        costItemId: this.filterCostItemId.map((c) => c.id),
        storeId: this.filterStoreId.map((s) => s.id),
      };

      if (this.viewMode === 'year') {
        const y = this.getFilterYearMoment();
        return {
          ...common,
          year: y ? y.format('YYYY') : '',
        };
      }

      // month view
      return {
        ...common,
        periodFrom: this.filterPeriodFrom || '',
        periodTo: this.filterPeriodTo || '',
      };
    },
    totalAllocated() {
      return this.items.reduce(
        (sum, i) => sum + (Number(i.allocatedAmount) || 0),
        0,
      );
    },
    totalUsed() {
      return this.items.reduce(
        (sum, i) => sum + (Number(i.usedAmount) || 0),
        0,
      );
    },
    totalRemaining() {
      return this.items.reduce(
        (sum, i) => sum + (Number(i.remainingAmount) || 0),
        0,
      );
    },
  },
  created() {
    const empInfoRaw = getData('employee_info');
    if (empInfoRaw) {
      try {
        const parsed = JSON.parse(empInfoRaw);
        this.currentEmployeeId = parsed?.id || parsed?._id || null;
      } catch (e) {
        this.currentEmployeeId = null;
      }
    }
    this.applyQueryFilters();
    this.fetchFilterOptions();
    this.fetchEmployees();
    this.fetchData();
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Kế toán', route: '#' },
      { title: 'Ngân sách' },
    ]);
  },
  methods: {
    checkPermission,
    makeToastSuccess,
    makeToastFaile,
    convertPrice,
    applyQueryFilters() {
      const q = this.$route?.query || {};
      const view = String(q.view || 'year').toLowerCase();
      this.viewMode = view === 'month' ? 'month' : 'year';
      this.page = q.page || 1;

      this.filterCompanyId = q.companyId ? Number(q.companyId) : null;
      this.filterStatus =
        q.status !== undefined && q.status !== '' ? Number(q.status) : null;
      this.filterCreatedBy = q.createdBy ? Number(q.createdBy) : null;
      this.filterKeyword = q.keyword ? String(q.keyword) : '';

      // Chỉ restore khi options đã load. Nếu chưa, fetchFilterOptions tự restore sau.
      const toIds = (val) =>
        (Array.isArray(val) ? val : val ? [val] : [])
          .map(Number)
          .filter((n) => n > 0);
      if (this.departmentDropdownOptions.length) {
        const ids = toIds(q.departmentId);
        this.filterDepartmentId = this.departmentDropdownOptions.filter((o) =>
          ids.includes(Number(o.id)),
        );
      }
      if (this.costItemDropdownOptions.length) {
        const ids = toIds(q.costItemId);
        this.filterCostItemId = this.costItemDropdownOptions.filter((o) =>
          ids.includes(Number(o.id)),
        );
      }
      if (this.storeDropdownOptions.length) {
        const ids = toIds(q.storeId);
        this.filterStoreId = this.storeDropdownOptions.filter((o) =>
          ids.includes(Number(o.id)),
        );
      }
      if (this.costGroupDropdownOptions.length) {
        const ids = toIds(q.costGroupId);
        this.filterCostGroupId = this.costGroupDropdownOptions.filter((o) =>
          ids.includes(Number(o.id)),
        );
      }

      if (this.viewMode === 'year') {
        const y = q.year ? String(q.year) : '';
        if (y) this.filterYear = moment(y, 'YYYY');
        this.filterPeriodFrom = '';
        this.filterPeriodTo = '';
      } else {
        this.filterPeriodFrom = q.periodFrom
          ? String(q.periodFrom).slice(0, 7)
          : '';
        this.filterPeriodTo = q.periodTo ? String(q.periodTo).slice(0, 7) : '';
      }
    },
    setViewMode(mode) {
      if (mode !== 'year' && mode !== 'month') return;
      if (this.viewMode === mode) return;
      this.viewMode = mode;
      if (mode === 'year' && !this.getFilterYearMoment()) {
        this.filterYear = moment(String(new Date().getFullYear()), 'YYYY');
      }
      // reset paging when switching mode
      this.$router
        .push({
          name: 'list-budget-lines',
          query: { ...this.paginationLinkParams, page: 1 },
        })
        .catch(() => {});
      this.fetchData();
    },
    /** Giá trị năm từ date-picker (moment hoặc string) */
    getFilterYearMoment() {
      const raw = this.filterYear;
      if (raw == null || raw === '') return null;
      if (moment.isMoment(raw)) {
        return raw.isValid() ? raw.clone().startOf('year') : null;
      }
      const m = moment(String(raw), ['YYYY', 'YYYY-MM-DD'], true);
      return m.isValid() ? m.clone().startOf('year') : null;
    },
    formatPeriod(val) {
      if (!val) return '';
      return moment(val).format('MM/YYYY');
    },
    formatYear(val) {
      if (!val) return '';
      return moment(val).format('YYYY');
    },
    formatDateTime(val) {
      if (!val) return '';
      return moment(val).format('DD/MM/YYYY HH:mm');
    },
    historyActionLabel(action) {
      return HISTORY_ACTION_TYPE_CODE[action] || String(action || '');
    },
    formatJson(val) {
      if (val === null || val === undefined) return '--';
      if (typeof val === 'string') return val || '--';
      try {
        const str = JSON.stringify(val);
        // avoid huge table cells
        return str.length > 2000 ? `${str.slice(0, 2000)}...` : str;
      } catch (e) {
        return String(val);
      }
    },
    statusVariant(val) {
      return BUDGET_STATUS_BADGE_VARIANT[val] || 'secondary';
    },
    statusName(val) {
      return BUDGET_STATUS_NAME[val] || '';
    },
    fetchFilterOptions() {
      const q = this.$route?.query || {};
      const toIds = (val) =>
        (Array.isArray(val) ? val : val ? [val] : []).map(Number).filter((n) => n > 0);
      // departments
      ApiService.query('departments/all').then(({ data }) => {
        const rows = data.data || [];
        this.departmentDropdownOptions = rows.map((d) => ({ id: d.id, name: d.name }));
        if (!this.filterDepartmentId.length) {
          const ids = toIds(q.departmentId);
          this.filterDepartmentId = this.departmentDropdownOptions.filter((o) =>
            ids.includes(Number(o.id)),
          );
        }
      });
      // cost items
      ApiService.get('v2/cost-items/all').then(({ data }) => {
        const rows = data.data || [];
        this.costItemDropdownOptions = rows.map((c) => ({ id: c.id, name: c.name }));
        if (!this.filterCostItemId.length) {
          const ids = toIds(q.costItemId);
          this.filterCostItemId = this.costItemDropdownOptions.filter((o) =>
            ids.includes(Number(o.id)),
          );
        }
      });
      // companies
      ApiService.get('/company').then(({ data }) => {
        const rows = data.data || [];
        this.companyOptions = [
          { value: null, text: '-- Doanh nghiệp --' },
          ...rows.map((c) => ({ value: c.id, text: c.name })),
        ];
      });
      // cost groups
      ApiService.get('v2/cost-groups/all').then(({ data }) => {
        const rows = data.data || [];
        this.costGroupDropdownOptions = rows.map((g) => ({ id: g.id, name: g.name }));
        if (!this.filterCostGroupId.length) {
          const ids = toIds(q.costGroupId);
          this.filterCostGroupId = this.costGroupDropdownOptions.filter((o) =>
            ids.includes(Number(o.id)),
          );
        }
      });
      // stores
      const params = {
        byEmployeeGroup: true,
      };
      ApiService.query('stores/getStores', { params }).then(({ data }) => {
        const rows = data.data || [];
        this.storeDropdownOptions = rows.map((s) => ({
          id: s.id,
          name: s.shortName ? `${s.name} (${s.shortName})` : s.name,
          shortName: s.shortName,
        }));
        if (!this.filterStoreId.length) {
          const ids = toIds(q.storeId);
          this.filterStoreId = this.storeDropdownOptions.filter((o) =>
            ids.includes(Number(o.id)),
          );
        }
      });
    },
    fetchEmployees() {
      ApiService.get('/employees/get-list-filter').then((response) => {
        const employees = response?.data?.data || [];
        this.employeeSource = employees.map((e) => {
          let suggestionName = e.fullName || e.name || '';
          if (e.code) suggestionName += ` (${e.code})`;
          const rawName = e.fullName || e.name || '';
          return {
            id: e.id,
            name: rawName,
            code: e.code || '',
            suggestionName,
            nameNoneSign: xoa_dau(rawName),
          };
        });
        this.employeeSuggestions = [...this.employeeSource];
        if (this.filterCreatedBy) {
          const found = this.employeeSource.find(
            (e) => Number(e.id) === Number(this.filterCreatedBy),
          );
          if (found) this.employeeModel = found.suggestionName;
        }
      });
    },
    onSelectedEmployee(option) {
      const item = option?.item || null;
      this.filterCreatedBy = item?.id || null;
      this.employeeModel = item ? item.suggestionName : '';
      this.employeeSuggestions = [...this.employeeSource];
    },
    onSearchEmployeeChange(text) {
      const t = text != null ? String(text) : '';
      this.employeeModel = t;
      if (!t.trim()) {
        this.filterCreatedBy = null;
        this.employeeSuggestions = [...this.employeeSource];
        return;
      }
      const tl = t.toLowerCase();
      this.employeeSuggestions = (this.employeeSource || [])
        .filter((item) => {
          return (
            (item.nameNoneSign || '').toLowerCase().indexOf(tl) > -1 ||
            (item.name || '').toLowerCase().indexOf(tl) > -1 ||
            (item.code || '').toLowerCase().indexOf(tl) > -1
          );
        })
        .slice(0, this.suggestLimit);
    },
    onFilter() {
      if (this.viewMode === 'year' && !this.getFilterYearMoment()) {
        this.makeToastFaile('Vui lòng chọn năm!');
        return;
      }
      this.$router
        .push({
          name: 'list-budget-lines',
          query: { ...this.paginationLinkParams, page: 1 },
        })
        .catch(() => {})
        .finally(() => this.fetchData());
    },
    canDeleteBudgetLine(item) {
      if (!item) return false;
      if (!this.currentEmployeeId) return false;
      if (Number(item.status) !== Number(BUDGET_STATUS.DRAFT)) return false;
      if (item.createdBy == null) return false;
      return Number(item.createdBy) === Number(this.currentEmployeeId);
    },
    confirmDeleteBudget(item) {
      if (!item?.id) return;
      showDeleteAlert(
        'Xác nhận xóa?',
        'Bạn có chắc muốn xóa ngân sách này?',
        item,
        (deleteItem) =>
          ApiService.delete(`v2/budget-lines/${deleteItem.id}`)
          .then(({ data }) => {
            this.makeToastSuccess(data.message);
            this.fetchData();
          })
          .catch((err) => handleError(err)),
      );
    },
    fetchData() {
      this.onLoading = true;
      this.page = this.$route.query.page || 1;
      const q = this.$route.query || {};
      const toIds = (val) =>
        (Array.isArray(val) ? val : val ? [val] : []).map(Number).filter((n) => n > 0);
      const params = {
        page: this.page,
        limit: 20,
        view: this.viewMode,
        departmentId: this.filterDepartmentId.length
          ? this.filterDepartmentId.map((d) => d.id)
          : toIds(q.departmentId),
        costItemId: this.filterCostItemId.length
          ? this.filterCostItemId.map((c) => c.id)
          : toIds(q.costItemId),
        storeId: this.filterStoreId.length
          ? this.filterStoreId.map((s) => s.id)
          : toIds(q.storeId),
        status: this.filterStatus !== null ? this.filterStatus : '',
        companyId: this.filterCompanyId || '',
        costGroupId: this.filterCostGroupId.length
          ? this.filterCostGroupId.map((g) => g.id)
          : toIds(q.costGroupId),
        createdBy: this.filterCreatedBy || '',
        keyword: this.filterKeyword || '',
        myApproval: this.filterMyApproval || '',
      };
      const normalizeMonth = (raw) => {
        const s = String(raw);
        const m = moment(
          s,
          ['YYYY-MM', 'YYYY-MM-DD', 'MM/YYYY', 'DD/MM/YYYY'],
          true,
        );
        if (m.isValid()) return m.format('YYYY-MM-DD');
        if (s.length >= 7) return `${s.slice(0, 7)}-01`;
        return `${s}-01`;
      };
      if (this.filterPeriodFrom)
        params.periodFrom = normalizeMonth(this.filterPeriodFrom);
      if (this.filterPeriodTo)
        params.periodTo = normalizeMonth(this.filterPeriodTo);

      if (this.viewMode === 'year') {
        delete params.periodFrom;
        delete params.periodTo;
        const y = this.getFilterYearMoment();
        if (!y) {
          this.makeToastFaile('Vui lòng chọn năm!');
          this.onLoading = false;
          return;
        }
        params.year = y.format('YYYY');
      }

      ApiService.query('v2/budget-lines', { params }).then(({ data }) => {
        if (data.status === 200) {
          this.items = data.data.dataset;
          this.numberOfPage = data.data.totalPage;
          this.totalRow = data.data.totalRow || 0;
        } else {
          this.makeToastFaile(data.message);
        }
        this.onLoading = false;
      });
    },
    declare() {
      this.$router.push({ name: 'declare-budget' });
    },
    importExcel() {
      this.$router.push({ name: 'import-budget' });
    },
    editItem(item) {
      if (!item?.id) return;
      if (this.viewMode === 'year') {
        this.$router.push({
          name: 'edit-budget-year-group',
          query: { id: item.id, from: this.$route.fullPath },
        });
        return;
      }
      this.$router.push({
        name: 'edit-budget-line',
        query: { id: item.id, from: this.$route.fullPath },
      });
    },
    viewHistory(item) {
      ApiService.get(`v2/budget-lines/${item.id}/history`).then(({ data }) => {
        if (data.status === 200) {
          this.historyItems = (data.data || []).map((h) => ({
            ...h,
            actionLabel: this.historyActionLabel(h.action),
            originalValueText: this.formatJson(h.originalValue),
            newValueText: this.formatJson(h.newValue),
          }));
          this.$refs.historyModal.show();
        }
      });
    },
    viewDetail(item) {
      if (this.viewMode === 'year') {
        this.$router.push({
          name: 'detail-budget-year-group',
          query: { id: item.id, from: this.$route.fullPath },
        });
      } else {
        this.$router.push({
          name: 'detail-budget-line',
          query: { id: item.id, from: this.$route.fullPath },
        });
      }
    },
    openApprovalModal(item, action) {
      this.approvalAction = action;
      this.approvalForm = { id: item.id, note: '' };
      this.$refs.approvalModal.show();
    },
    patchBudgetLineChangeStatus(id, status, note) {
      return ApiService.patch(`v2/budget-lines/change-status/${id}`, {
        status,
        note: note != null ? String(note) : '',
      });
    },
    confirmApproval(evt) {
      evt.preventDefault();

      const id = this.approvalForm.id;
      if (!id) return;

      const isReject =
        this.approvalAction === WORKFLOW_APPROVAL_STEP_STATUS.REJECTED;
      const note = this.approvalForm.note || '';
      if (isReject && !note.trim()) {
        this.makeToastFaile('Vui lòng nhập lý do không duyệt!');
        return;
      }

      const status = isReject
        ? WORKFLOW_APPROVAL_STEP_STATUS.REJECTED
        : WORKFLOW_APPROVAL_STEP_STATUS.APPROVED;
      this.patchBudgetLineChangeStatus(id, status, note)
        .then(({ data }) => {
          this.makeToastSuccess(data.message);
          this.fetchData();
          this.$refs.approvalModal.hide();
        })
        .catch((err) => handleError(err));
    },
  },
};
</script>
